// `currentUser` is the logged-in user ({ name, email }) from useAuth — the name/email in
// the company box are theirs, not a fixed contact. Quotes carry no stored creator (the
// `createdBy` the FE sends is silently dropped by strict mode in quoteSchema), so the PDF
// always reflects whoever is generating it, by design — see the company box below.
export const generatePdfTemplate = ({ items, customerData, selectedContact, quoteNumber, discount, infeedItems, outfeedItems, newItems, additionalItems, grandTotal, sawDetails, workBedHeight, infeedModuleImages = [], outfeedModuleImages = [], infeedEl1, outfeedEl1, sawEl1, arrowEl1, quoteDetails, currentUser }) => {
  if (!items || items.length === 0) return '';

  const quote = items[0];
  
  // Helper function to format prices as whole numbers with commas
  const formatPrice = (price) => { const num = parseFloat(price) || 0; return '$' + Math.round(num).toLocaleString('en-US'); };

  const isOptionItem = (item) => ['MT', 'TA', 'STK', 'PW', 'VR', 'CP'].includes(item?.specialLabel);

  const splitSpecialItems = (itemsList = []) => {
    const primary = [];
    const options = [];
    itemsList.forEach((item) => { if (isOptionItem(item)) { options.push(item); } else { primary.push(item); } });
    return { primary, options };
  };

  const renderTableRows = (itemsList, fallbackType) =>
    itemsList
      .map((item) => {
        const discountValue = item.discount > 0 ? parseFloat(item.discount) : parseFloat(discount || 0);
        const listPrice = parseFloat(item.listPrice || 0);
        const actualPrice = listPrice * (1 - discountValue / 100);
        const extPrice = (item.qty || 0) * actualPrice;

        return `
          <tr>
            <td class="col-index wrap-text">${item.globalIndex}</td>
            <td class="col-type wrap-text">${item.boxType || fallbackType}</td>
            <td class="col-part wrap-text">${item.partName || ''}</td>
            <td class="col-desc wrap-text">${item.description || ''}</td>
            <td class="col-qty wrap-text">${item.qty || 0}</td>
            <td class="col-price wrap-text">${formatPrice(listPrice)}</td>
            <td class="col-disc wrap-text">${discountValue}%</td>
            <td class="col-actual wrap-text">${formatPrice(actualPrice)}</td>
            <td class="col-ext wrap-text">${formatPrice(extPrice)}</td>
          </tr>
        `;
      })
      .join('');

  const { primary: primaryInfeedItems, options: optionInfeedItems } = splitSpecialItems(infeedItems);
  const { primary: primaryOutfeedItems, options: optionOutfeedItems } = splitSpecialItems(outfeedItems);

  // Count non-spacer conveyors for layout page totals
  const totalInfeedCount = primaryInfeedItems.filter(item => !(item.imageName || '').toUpperCase().includes('SPACER') && !(item.description || '').toUpperCase().includes('SPACER')).reduce((sum, item) => sum + (item.qty || 1), 0);
  const totalOutfeedCount = primaryOutfeedItems.filter(item => !(item.imageName || '').toUpperCase().includes('SPACER') && !(item.description || '').toUpperCase().includes('SPACER')).reduce((sum, item) => sum + (item.qty || 1), 0);

  // Deduplicate by part number so each part (e.g. BABS-10-NPFL-24) appears only once at end of PDF
  const uniqueByPartNumber = (arr) => {
    const seen = new Set();
    return (arr || []).filter((img) => {
      const key = (img.part_number || img.partName || '').toString().trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const uniqueInfeedModules = uniqueByPartNumber(infeedModuleImages);
  const uniqueOutfeedModules = uniqueByPartNumber(outfeedModuleImages);

  return `
    <html>
      <head>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; }
          
          /* Page structure */
          .page { position: relative; min-height: 100vh; display: flex; flex-direction: column; page-break-after: always; }
          .page:last-child { page-break-after: auto; }

          /* Landscape layout page for combined conveyor layouts */
          .page-landscape { position: relative; display: flex; flex-direction: column; page-break-after: always; page: landscape; }
          .page-landscape:last-child { page-break-after: auto; }
          @page landscape { size: landscape; margin: 10px; }
          @page { margin: 15px; }

          .page-content { flex: 1; padding: 20px; display: flex; flex-direction: column; }

          .attachment-header { text-align: center; font-weight: bold; font-size: 16px; margin-bottom: 5px; color: #333; margin-top: -20px; }
   
          table { width: 100%; border-collapse: collapse; margin-top: 0; table-layout: fixed; font-size: 12px; }
th, td { 
  border: 1px solid #ddd; 
  padding: 6px; 
  text-align: left; 
  vertical-align: top; 
  word-wrap: break-word; 
  font-size: 18px; /* added */
}
          th { background-color: #f2f2f2; white-space: nowrap; font-weight: bold; font-size: 12px; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; position: relative; }
          .header-title { position: absolute; left: 50%; transform: translateX(-50%); text-align: center; top: -3px; }
          .total { font-weight: bold; text-align: right; margin-top: 20px; }
.section-heading { 
  margin: 10px 0 8px 0; 
  font-size: 24px; /* was 16px */
  color: #333; 
}
         .company-info { 
  margin-bottom: 10px; 
  margin-top: -10px; /* reduced so it doesn’t clip */
  display: flex; 
  flex-direction: column; 
  align-items: center; 
} 
          .company-info h2 { font-size: 24px; font-weight: bold; color: #333; margin: 0; padding: 0; }

          .customer-info, .machine-info { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 20px; background: #f9f9f9; padding: 15px; border-radius: 8px; border: 1px solid #ddd; margin-top: 5px; width: 100%; max-width: 100%; }
          .customer-info p, .machine-info p { margin: 0; font-size: 22px; }
          .customer-info strong, .machine-info strong { color: #333; }
          
          .section-header { background-color: #e9e9e9; font-weight: bold; font-size: 12px; text-align: center; padding: 8px 0; }

          .section-header-content { display: flex; align-items: center; justify-content: center; width: 100%; }

          .section-header-line { flex-grow: 1; height: 1px; border-bottom: 1px dotted #333; margin: 0 10px; }

          .section-header-text { padding: 0 10px; white-space: nowrap; }
          
          .col-index { width: 5%; }
          .col-type { width: 8%; }
          .col-part { width: 15%; }
          .col-desc { width: 26%; } 
          .col-qty { width: 6%; }
          .col-price { width: 10%; }
          .col-disc { width: 10%; } 
          .col-actual { width: 10%; }
          .col-ext { width: 10%; }

          .wrap-text { white-space: normal; word-break: break-word; overflow-wrap: break-word; }

          .additional-details-table { width: 100%; border-collapse: collapse; margin-top: 0; table-layout: fixed; font-size: 12px; }
          .additional-details-table th, 
          .additional-details-table td { border: 1px solid #ddd; padding: 6px; text-align: left; vertical-align: top; word-wrap: break-word; }
          .additional-details-table th { background-color: #f2f2f2; white-space: nowrap; font-weight: bold; font-size: 12px; }
          
          .additional-details-table .col-index { width: 4.2%; }
          .additional-details-table .col-detail { width: 35.8%; }
          .additional-details-table .col-value { width: 60%; }

          .company-boxes-container { display: flex; justify-content: space-between; margin-bottom: 20px; gap: 20px; width: 350px; }
          
          .company-box { flex: 1; border: 1px solid #ddd; border-radius: 8px; padding: 15px; background-color: #f9f9f9; min-width: 0; }
          
          .company-box h3 { margin-top: 0; margin-bottom: 10px; font-size: 16px; color: #333; }
          
          .company-box p { margin: 5px 0; font-size: 14px; }
          
          .email-link { color: #0066cc; text-decoration: underline; cursor: pointer; }

          .layout-container { display: flex; flex-direction: column; align-items: center; margin-bottom: 20px; }

          .layout-title { font-weight: bold; font-size: 16px; margin-bottom: 15px; }

          .layout-image-container { display: flex; flex-direction: row; flex-wrap: nowrap; width: 100%; min-height: 200px; border: 1px dashed #ccc; border-radius: 6px; background-color: #f9f9f9; box-sizing: border-box; justify-content: center; align-items: center; margin-top: 20px; gap: 20px; }

          .infeed-row {
            // justify-content: flex-end;
          }

          .infeed-images-only { display: flex; flex-direction: row-reverse; align-items: center; gap: 0px; }

          .outfeed-images-only { display: flex; flex-direction: row; align-items: center; gap: 0px; }
          
          .outfeed-images-only div[style*="position: absolute"]:has(img[alt="Motor"]) { margin-top: 19px !important; }
          
          [id^="left-module-"], [id^="right-module-"] { position: relative !important; height: 120px !important; }

          [id^="left-module-"] img[alt="STK component"],
          [id^="right-module-"] img[alt="STK component"] { z-index: 10 !important; }

          .outfeed-row {
            // justify-content: flex-start;
          }

          .infeed-row, .outfeed-row { gap: 0 !important; }

          .saw-image-only { margin: 0 !important; padding: 0 !important; }

          #saw-module { margin-top: 0 !important; transform: none !important; height: 120px !important; }

          .infeed-images-only [id^="left-module-"] > div > div[style*="translateY"],
          .outfeed-images-only [id^="right-module-"] > div > div[style*="translateY"] { transform: none !important; }

          .saw-image-only .saw-image-only { display: contents !important; }
          
          .arrow-image-only { display: flex; flex-direction: column; align-items: center; justify-content: center; margin-top: 20px; gap: 8px; width: 100%; }
          
          .arrow-image-only div[style*="background-color: rgb(255, 222, 33)"] { height: 22px !important; }
          
          /* Full Layout Styles */
          .full-layout-container { display: flex; flex-direction: column; align-items: center; margin: 5px 0; flex: 1; }

          .full-layout-title { font-weight: bold; font-size: 14px; margin-bottom: 8px; }

          .full-layout-content { display: flex; justify-content: center; align-items: center; width: 100%; max-width: 95%; margin: 0 auto; gap: 5px; }

          .full-layout-section { flex: 0 0 auto; display: flex; flex-direction: column; align-items: center; }

          .full-layout-saw { flex: 0 0 auto; padding: 0 5px; display: flex; flex-direction: column; align-items: center; }

          /* Combined landscape layouts page */
          .combined-layouts-page { padding: 15px 20px; display: flex; flex-direction: column; height: 100%; }
          .combined-layouts-page .attachment-header { margin-bottom: 4px; font-size: 14px; }
          .layout-counts { display: flex; justify-content: center; gap: 40px; margin-bottom: 8px; font-size: 11px; font-weight: 600; color: #444; letter-spacing: 0.5px; }
          .combined-layouts-page .full-layout-container { margin: 8px 0; }
          .combined-layouts-page .layout-container { margin-bottom: 10px; }
          .combined-layouts-page .layout-title { font-size: 13px; margin-bottom: 8px; }
          .combined-layouts-page .layout-image-container { min-height: 130px; margin-top: 8px; padding: 8px; }
          .combined-layouts-page .arrow-image-only { margin-top: 8px; gap: 5px; }
          .combined-layouts-page .arrow-image-only div[style*="background-color: rgb(255, 222, 33)"] { height: 16px !important; }
          .combined-layouts-page [id^="left-module-"], .combined-layouts-page [id^="right-module-"] { height: 90px !important; }
          .combined-layouts-page #saw-module { height: 90px !important; }
          .combined-layouts-page .full-layout-content { max-width: 100%; }
          .layouts-divider { border: none; border-top: 1px dashed #aaa; margin: 20px 0; }
          
          .module-page { display: flex; flex-direction: column; height: 100%; }
          .module-header { padding: 8px 8px; text-align: center; }
          .module-title { font-size: 18px; font-weight: bold; color: #111; }
          
          .module-top-section { display: flex; gap: 20px; margin-bottom: 20px; flex: 1; }
          
          .module-details-container { flex: 1; display: flex; flex-direction: column; gap: 16px; }
          
          .module-image-container { flex: 1; display: flex; align-items: center; justify-content: center; border: 1px dashed #ccc; border-radius: 6px; background-color: #f9f9f9; min-height: 300px; }
          
          .module-image { max-width: 100%; max-height: 100%; object-fit: contain; }
          
          .module-info-box { border: 1px solid #ddd; border-radius: 6px; padding: 12px; }
          
          .selected-options-section { width: 100%; border: 1px solid #ddd; border-radius: 6px; padding: 16px; margin-top: 10px; box-sizing: border-box; }

          .selected-options-title { font-weight: bold; margin-bottom: 15px; font-size: 16px; color: #333; border-bottom: 1px dashed #ccc; padding-bottom: 8px; }

          .selected-options-list { display: flex; flex-direction: column; gap: 10px; }

          .selected-option { display: flex; align-items: flex-start; width: 100%; }

          .selected-option-checkbox { width: 18px; height: 18px; border: 1px solid #333; margin-right: 12px; position: relative; flex-shrink: 0; margin-top: 2px; background-color: transparent; }

          .selected-option-checkbox::after { content: "✓"; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); color: black; font-size: 14px; font-weight: bold; }

          .selected-option-text { font-size: 14px; line-height: 1.4; margin-top: 3px; flex: 1; word-wrap: break-word; overflow-wrap: break-word; white-space: normal; }
          
          .table-container { margin-bottom: 30px; margin-top: 40px; page-break-inside: avoid; break-inside: avoid; }

          .option-container { margin-bottom: 30px; margin-top: -30px; page-break-inside: avoid; break-inside: avoid; }
          
          .table-title { background-color: #f2f2f2; padding: 6px 20px; font-size: 24px; font-weight: bold; color: #333; text-align: center; display: flex; align-items: center; justify-content: center; }
          
          .table-title .section-header-line { flex-grow: 1; height: 1px; border-bottom: 1px dotted #333; margin: 0 10px; }
          
          .table-title .section-header-text { padding: 0 10px; white-space: nowrap; }
          
        </style>
      </head>
      <body>
        <!-- Page 1: Main Quote -->
        <div class="page">
          <div class="page-content">
            <div class="company-info">
              <h2>${"BAD ASS BAND SAWS"}</h2>
            </div>

            <!-- Company boxes section -->
            <div class="company-boxes-container">
              <!-- Company address and phone are fixed; the name and email are whoever is
                   logged in and generating this PDF (they were hardcoded to Richard Rose,
                   so every quote showed him regardless of author). Each line is omitted
                   rather than rendered empty if the user hasn't loaded, which keeps a
                   half-hydrated session from printing a blank line or a dead mailto:. -->
              <div class="company-box">
                <h3>Bad Ass Band Saws</h3>
                <p>8637 Windfern Road</p>
                <p>Houston, TX 77064 USA</p>
                ${currentUser?.name ? `<p>${currentUser.name}</p>` : ''}
                ${currentUser?.email ? `<p><a href="mailto:${currentUser.email}" class="email-link">${currentUser.email}</a></p>` : ''}
                <p>(713) 424-9424</p>
              </div>
            </div>

            <div class="header">
              <div>
                <h4>Quote#: ${quoteNumber || ""}</h4>
                
                <!-- Customer Details Section -->
                <h3 class="section-heading">Customer Details:</h3>
                <div class="customer-info">
                  <p><strong>Customer:</strong> ${customerData?.customer || "" }</p>
                  ${(() => {
                    // If a non-primary contact is selected, show that location's address/contact
                    // instead of the default customer billing block.
                    if (selectedContact && selectedContact.source === 'additional') {
                      return `
                        ${selectedContact.location ? `<p><strong>Location:</strong> ${selectedContact.location}</p>` : ""}
                        ${selectedContact.email ? `<p><strong>Email:</strong> <a href="mailto:${selectedContact.email}" class="email-link">${selectedContact.email}</a></p>` : ""}
                        ${selectedContact.phone ? `<p><strong>Phone:</strong> ${selectedContact.phone}</p>` : ""}
                        ${selectedContact.name ? `<p><strong>Primary Contact:</strong> ${selectedContact.name}</p>` : ""}
                      `;
                    }
                    return `
                      ${customerData?.title ? `<p><strong>Title:</strong> ${customerData.title}</p>` : ""}
                      ${customerData?.email ? `<p><strong>Email:</strong> <a href="mailto:${customerData.email}" class="email-link">${customerData.email}</a></p>` : ""}
                      ${customerData?.phone ? `<p><strong>Phone:</strong> ${customerData.phone}</p>` : ""}
                      ${customerData?.address ? `<p><strong>Address:</strong> ${customerData.address}</p>` : ""}
                      ${customerData?.city ? `<p><strong>City:</strong> ${customerData.city}</p>` : ""}
                      ${customerData?.state ? `<p><strong>State:</strong> ${customerData.state}</p>` : ""}
                      ${customerData?.zip ? `<p><strong>Zip:</strong> ${customerData.zip}</p>` : ""}
                      ${(selectedContact?.name || customerData?.primary_contact) ? `<p><strong>Primary Contact:</strong> ${selectedContact?.name || customerData.primary_contact}</p>` : ""}
                    `;
                  })()}
                </div>
                
                <!-- SAW Details Section -->
                <h3 class="section-heading">Saw Details:</h3>
                <div class="machine-info">
                  ${sawDetails ? ` <p><strong>Saw Brand & Model:</strong> ${ sawDetails.brand || "Unknown Brand" } ${sawDetails.model ? `- ${sawDetails.model}` : ""}</p> ` : "<p><strong>Saw Brand & Model:</strong> Not specified</p>" }
                  ${workBedHeight ? `<p><strong>Work Bed Height:</strong> ${workBedHeight}"</p>` : "<p><strong>Work Bed Height:</strong> Not specified</p>" }
                </div>
                
                ${quote?.customer_po ? `<p style="margin-top:10px;"><strong>PO Number:</strong> ${quote.customer_po}</p>` : "" }
              </div>

              <!-- Center Title -->
              <div class="header-title">
                <h3>Customer Quote Details</h3>
              </div>

              <div>
                <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
              </div>
            </div>

            <!-- Infeed Quote Tables -->
            ${
              primaryInfeedItems.length > 0 ? `
              <div class="table-container">
                <div class="table-title">
                  <div class="section-header-line"></div>
                  <div class="section-header-text">Primary Quote InFeed</div>
                  <div class="section-header-line"></div>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th class="col-index">Index</th>
                      <th class="col-type">Type</th>
                      <th class="col-part">Part #</th>
                      <th class="col-desc">Description</th>
                      <th class="col-qty">Qty</th>
                      <th class="col-price">List Price</th>
                      <th class="col-disc">Discount</th>
                      <th class="col-actual">Act Price</th>
                      <th class="col-ext">Ext Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${ renderTableRows(primaryInfeedItems, "Infeed") }
                  </tbody>
                </table>
              </div>
              ` : ""
            }
            ${
              optionInfeedItems.length > 0 ? `
              <div class="option-container">
                <div class="table-title">
                  <div class="section-header-line"></div>
                  <div class="section-header-text">Primary Quote InFeed Options</div>
                  <div class="section-header-line"></div>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th class="col-index">Index</th>
                      <th class="col-type">Type</th>
                      <th class="col-part">Part #</th>
                      <th class="col-desc">Description</th>
                      <th class="col-qty">Qty</th>
                      <th class="col-price">List Price</th>
                      <th class="col-disc">Discount</th>
                      <th class="col-actual">Act Price</th>
                      <th class="col-ext">Ext Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${ renderTableRows(optionInfeedItems, "Infeed") }
                  </tbody>
                </table>
              </div>
              ` : ""
            }

            <!-- Outfeed Quote Tables -->
            ${
              primaryOutfeedItems.length > 0 ? `
              <div class="table-container">
                <div class="table-title">
                  <div class="section-header-line"></div>
                  <div class="section-header-text">Primary Quote OutFeed</div>
                  <div class="section-header-line"></div>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th class="col-index">Index</th>
                      <th class="col-type">Type</th>
                      <th class="col-part">Part #</th>
                      <th class="col-desc">Description</th>
                      <th class="col-qty">Qty</th>
                      <th class="col-price">List Price</th>
                      <th class="col-disc">Discount</th>
                      <th class="col-actual">Act Price</th>
                      <th class="col-ext">Ext Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${ renderTableRows(primaryOutfeedItems, "Outfeed") }
                  </tbody>
                </table>
              </div>
              ` : ""
            }
            ${
              optionOutfeedItems.length > 0 ? `
              <div class="option-container">
                <div class="table-title">
                  <div class="section-header-line"></div>
                  <div class="section-header-text">Primary Quote OutFeed Options</div>
                  <div class="section-header-line"></div>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th class="col-index">Index</th>
                      <th class="col-type">Type</th>
                      <th class="col-part">Part #</th>
                      <th class="col-desc">Description</th>
                      <th class="col-qty">Qty</th>
                      <th class="col-price">List Price</th>
                      <th class="col-disc">Discount</th>
                      <th class="col-actual">Act Price</th>
                      <th class="col-ext">Ext Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${ renderTableRows(optionOutfeedItems, "Outfeed") }
                  </tbody>
                </table>
              </div>
              ` : ""
            }
            
            <!-- New Items Table -->
            ${
              newItems.length > 0 ? `
              <div class="table-container">
                <div class="table-title">
                  <div class="section-header-line"></div>
                  <div class="section-header-text">New Items</div>
                  <div class="section-header-line"></div>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th class="col-index">Index</th>
                      <th class="col-type">Type</th>
                      <th class="col-part">Part #</th>
                      <th class="col-desc">Description</th>
                      <th class="col-qty">Qty</th>
                      <th class="col-price">List Price</th>
                      <th class="col-disc">Discount</th>
                      <th class="col-actual">Act Price</th>
                      <th class="col-ext">Ext Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${renderTableRows(newItems, "-")}
                  </tbody>
                </table>
              </div>
              ` : ""
            }
            
            <!-- Additional Quote Details Section - Separate Table -->
            ${
              additionalItems && additionalItems.length > 0
                ? `
              <div class="table-container">
                <div class="table-title">
                  <div class="section-header-line"></div>
                  <div class="section-header-text">Additional Quote Details</div>
                  <div class="section-header-line"></div>
                </div>
                <table class="additional-details-table">
                  <thead>
                    <tr>
                      <th class="col-index">Index</th>
                      <th class="col-detail">Detail</th>
                      <th class="col-value">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${additionalItems
                      .map(
                        (item) => `
                      <tr>
                        <td class="col-index wrap-text">${item.globalIndex}</td>
                        <td class="col-detail wrap-text">${item.key}</td>
                        <td class="col-value wrap-text">${item.value}</td>
                      </tr>
                    `
                      )
                      .join("")}
                  </tbody>
                </table>
              </div>
            `
                : ""
            }
            
            <p class="total">Grand Total: ${formatPrice(grandTotal)}</p>
          </div>
        </div>

        <!-- Combined Layouts Page (Landscape) - Full Layout + Infeed Layout + Outfeed Layout -->
        ${
          (infeedEl1?.innerHTML || outfeedEl1?.innerHTML) &&
          `<div class="page-landscape">
          <div class="combined-layouts-page">
            <div class="attachment-header">Quote Attachment</div>
            <div class="layout-counts">
              <span>TOTAL INFEED: ${totalInfeedCount}</span>
              <span>TOTAL OUTFEED: ${totalOutfeedCount}</span>
            </div>

            <!-- Full Layout -->
            <div class="full-layout-container">
              <div class="full-layout-title">${quoteNumber || ""} - PRIMARY QUOTE - FULL LAYOUT</div>
              <div class="full-layout-content">
                <div class="full-layout-section">
                  ${ infeedEl1 ? typeof infeedEl1 === "string" ? infeedEl1 : infeedEl1.outerHTML || infeedEl1.innerHTML : "" }
                </div>
                <div class="full-layout-saw">
                  ${ sawEl1 ? typeof sawEl1 === "string" ? sawEl1 : sawEl1.outerHTML || sawEl1.innerHTML : "" }
                </div>
                <div class="full-layout-section">
                  ${ outfeedEl1 ? typeof outfeedEl1 === "string" ? outfeedEl1 : outfeedEl1.outerHTML || outfeedEl1.innerHTML : "" }
                </div>
              </div>
            </div>

            <hr class="layouts-divider" />

            <!-- Infeed Layout -->
            ${ infeedEl1?.innerHTML ? `
            <div class="layout-container">
              <div class="layout-title">${quoteNumber || ""} - PRIMARY QUOTE - INFEED LAYOUT</div>
              <div class="layout-image-container infeed-row">
                ${ infeedEl1 ? typeof infeedEl1 === "string" ? infeedEl1 : infeedEl1.outerHTML || infeedEl1.innerHTML : "" }
                ${ sawEl1 ? '<div class="saw-image-only">' + (typeof sawEl1 === "string" ? sawEl1 : sawEl1.outerHTML || sawEl1.innerHTML) + '</div>' : "" }
              </div>
              ${ arrowEl1 ? '<div class="arrow-image-only">' + (typeof arrowEl1 === "string" ? arrowEl1 : arrowEl1.outerHTML || arrowEl1.innerHTML) + '</div>' : "" }
            </div>

            <hr class="layouts-divider" />
            ` : "" }

            <!-- Outfeed Layout -->
            ${ outfeedEl1?.innerHTML ? `
            <div class="layout-container">
              <div class="layout-title">${quoteNumber || ""} - PRIMARY QUOTE - OUTFEED LAYOUT</div>
              <div class="layout-image-container outfeed-row">
                ${ sawEl1 ? '<div class="saw-image-only">' + (typeof sawEl1 === "string" ? sawEl1 : sawEl1.outerHTML || sawEl1.innerHTML) + '</div>' : "" }
                ${ outfeedEl1 ? typeof outfeedEl1 === "string" ? outfeedEl1 : outfeedEl1.outerHTML || outfeedEl1.innerHTML : "" }
              </div>
              ${ arrowEl1 ? '<div class="arrow-image-only">' + (typeof arrowEl1 === "string" ? arrowEl1 : arrowEl1.outerHTML || arrowEl1.innerHTML) + '</div>' : "" }
            </div>
            ` : "" }

          </div>
        </div>`
        }

        ${
          uniqueInfeedModules.length > 0
            ? uniqueInfeedModules
                .map((img, idx) => {
                  // Generate selected options HTML based on module properties
                  let selectedOptionsHtml = "";

                  if (img.motor_yn === "YES") {
                    const hpLabel = img.motor_hp_value || "3HP";
                    const motorText = `${hpLabel} MOTOR/ GEARBOX/ INVERTER/ CONTROLS FOR POWERED MODULES`;
                    selectedOptionsHtml += `
              <div class="selected-option">
                <div class="selected-option-checkbox"></div>
                <div class="selected-option-text">${motorText}</div>
              </div>
            `;
                  }

                  if (img.vertical_roller_yn === "YES") {
                    selectedOptionsHtml += `
              <div class="selected-option">
                <div class="selected-option-checkbox"></div>
                <div class="selected-option-text">VERTICAL ALIGNMENT ROLLERS TO KEEP MATERIALS IN LINE</div>
              </div>
            `;
                  }

                  if (img.coolant_pan_yn === "YES") {
                    selectedOptionsHtml += `
              <div class="selected-option">
                <div class="selected-option-checkbox"></div>
                <div class="selected-option-text">COOLANT CATCH PAN TO COLLECT AND RECOVER CUTTING FLUID</div>
              </div>
            `;
                  }

                  // If no options are selected, show a message
                  if (selectedOptionsHtml === "") {
                    selectedOptionsHtml =
                      '<div class="selected-option-text">No options selected</div>';
                  }

                  return `
            <div class="page">
              <div class="page-content">
                <div class="attachment-header">Quote Attachment</div>
                <div class="module-page">
                  <div class="module-header">
                    <h2 class="module-title">PRIMARY QUOTE - CONVEYOR INFEED MODULE DETAILS</h2>
                  </div>
                  
                  <!-- First Section: Details and Image Side by Side -->
                  <div class="module-top-section">
                    <div class="module-details-container">
                      <div class="module-info-box">
                        <div style="font-weight:bold;">INFEED MODULE #${
                          idx + 1
                        }</div>
                      </div>
                      <div class="module-info-box">
                        <div style="margin-bottom:6px;"><strong>PART NUMBER:</strong></div>
                        <div>${
                          img.part_number || "N/A"
                        }</div> <!-- Display part number -->
                      </div>
                      <div class="module-info-box">
                        <div style="white-space:pre-wrap; line-height:1.3;"><strong>PRODUCT DETAILS:</strong></div>
                        <div style="white-space:pre-wrap; line-height:1.3; margin-top:6px;">${
                          img.moduleDescription || "No description available"
                        }</div>
                      </div>
                    </div>
                    <div class="module-image-container">
                      ${
                        img.isoImageUrl
                          ? `<img class="module-image" src="${
                              img.isoImageUrl
                            }" alt="${
                              img.isoFilename || `Infeed Module ${idx + 1}`
                            }" />`
                          : `<div class="no-image">No ISO image available</div>`
                      }
                    </div>
                  </div>
                  
                  <!-- Second Section: Selected Options Full Width -->
                  <div class="selected-options-section">
                    <div class="selected-options-title">SELECTED OPTIONS</div>
                    <div class="selected-options-list">
                      ${selectedOptionsHtml}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          `;
                })
                .join("")
            : ""
        }

        ${
          uniqueOutfeedModules.length > 0
            ? uniqueOutfeedModules
                .map((img, idx) => {
                  // Generate selected options HTML based on module properties
                  let selectedOptionsHtml = "";

                  if (img.motor_yn === "YES") {
                    const hpLabel = img.motor_hp_value || "3HP";
                    const motorText = `${hpLabel} MOTOR/ GEARBOX/ INVERTER/ CONTROLS FOR POWERED MODULES`;
                    selectedOptionsHtml += `
              <div class="selected-option">
                <div class="selected-option-checkbox"></div>
                <div class="selected-option-text">${motorText}</div>
              </div>
            `;
                  }

                  if (img.vertical_roller_yn === "YES") {
                    selectedOptionsHtml += `
              <div class="selected-option">
                <div class="selected-option-checkbox"></div>
                <div class="selected-option-text">VERTICAL ALIGNMENT ROLLERS TO KEEP MATERIALS IN LINE</div>
              </div>
            `;
                  }

                  if (img.coolant_pan_yn === "YES") {
                    selectedOptionsHtml += `
              <div class="selected-option">
                <div class="selected-option-checkbox"></div>
                <div class="selected-option-text">COOLANT CATCH PAN TO COLLECT AND RECOVER CUTTING FLUID</div>
              </div>
            `;
                  }

                  // If no options are selected, show a message
                  if (selectedOptionsHtml === "") {
                    selectedOptionsHtml =
                      '<div class="selected-option-text">No options selected</div>';
                  }

                  return `
            <div class="page">
              <div class="page-content">
                <div class="attachment-header">Quote Attachment</div>
                <div class="module-page">
                  <div class="module-header">
                    <h2 class="module-title">PRIMARY QUOTE - CONVEYOR OUTFEED MODULE DETAILS</h2>
                  </div>
                  
                  <!-- First Section: Details and Image Side by Side -->
                  <div class="module-top-section">
                    <div class="module-details-container">
                      <div class="module-info-box">
                        <div style="font-weight:bold;">OUTFEED MODULE #${
                          idx + 1
                        }</div>
                      </div>
                      <div class="module-info-box">
                        <div style="margin-bottom:6px;"><strong>PART NUMBER:</strong></div>
                        <div>${
                          img.part_number || "N/A"
                        }</div> <!-- Display part number -->
                      </div>
                      <div class="module-info-box">
                        <div style="white-space:pre-wrap; line-height:1.3;"><strong>PRODUCT DETAILS:</strong></div>
                        <div style="white-space:pre-wrap; line-height:1.3; margin-top:6px;">${
                          img.moduleDescription || "No description available"
                        }</div>
                      </div>
                    </div>
                    <div class="module-image-container">
                      ${
                        img.isoImageUrl
                          ? `<img class="module-image" src="${
                              img.isoImageUrl
                            }" alt="${
                              img.isoFilename || `Outfeed Module ${idx + 1}`
                            }" />`
                          : `<div class="no-image">No ISO image available</div>`
                      }
                    </div>
                  </div>
                  
                  <!-- Second Section: Selected Options Full Width -->
                  <div class="selected-options-section">
                    <div class="selected-options-title">SELECTED OPTIONS</div>
                    <div class="selected-options-list">
                      ${selectedOptionsHtml}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          `;
                })
                .join("")
            : ""
        }
      </body>
    </html>
  `;
};
